import { Dtos, SuretyAccountId, TagId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { bondPreviewsQueryKey } from "../../bond/bond.keys";
import { bondRequestPreviewsQueryKey } from "../../bond-request/bond-request.keys";
import { accountPreviewsQueryKey } from "../../previews/previews.keys";
import { adminEntityTagsQueryKey } from "../entity.keys";
import { EntityId, EntityType } from "./types";

export const useAdminAttachTagToEntity = () => {
	const queryClient = useQueryClient();
	const { post } = useRequest();

	const mutation = useMutation({
		mutationFn: async ({
			tagId,
			entityType,
			entityId,
			accountId,
		}: {
			tagId: TagId;
			entityType: EntityType;
			entityId: EntityId;
			accountId?: SuretyAccountId;
		}) => {
			const base = accountId
				? `/v2/admin/surety/accounts/${accountId}/${entityType}s/${entityId}`
				: `/v2/admin/surety/${entityType}s/${entityId}`;
			await post<any>(`${base}/tags/${tagId}`);
		},
		onMutate: async ({ tagId, entityType, entityId }) => {
			await queryClient.cancelQueries({
				queryKey: adminEntityTagsQueryKey(entityType, entityId),
			});

			const previousTags = queryClient.getQueryData<Dtos.EntityTag.GetByEntity.Response>(
				adminEntityTagsQueryKey(entityType, entityId),
			);
			queryClient.setQueryData<Dtos.EntityTag.GetByEntity.Response>(
				adminEntityTagsQueryKey(entityType, entityId),
				(old) => {
					return [...(old ?? []), { id: tagId, displayName: "" } as Dtos.Tag.Get.Response].sort(
						(a, b) => a.displayName.localeCompare(b.displayName),
					);
				},
			);

			return { previousTags };
		},
		onError: (_err, { entityId, entityType }, context) => {
			queryClient.setQueryData(
				adminEntityTagsQueryKey(entityType, entityId),
				context?.previousTags,
			);
		},
		onSuccess: (_data, { entityId, entityType }) => {
			queryClient.invalidateQueries({
				queryKey: adminEntityTagsQueryKey(entityType, entityId),
			});
			switch (entityType) {
				case "account":
					queryClient.invalidateQueries({ queryKey: accountPreviewsQueryKey() });
					break;
				case "quote":
					queryClient.invalidateQueries({ queryKey: bondRequestPreviewsQueryKey() });
					break;
				case "bond":
					queryClient.invalidateQueries({ queryKey: bondPreviewsQueryKey() });
					break;
			}
		},
	});

	return {
		attachTagToEntity: mutation.mutate,
		attachTagToEntityLoading: mutation.isPending,
		attachTagToEntityError: mutation.error,
		dismissAttachTagToEntityError: () => {
			mutation.reset();
		},
	};
};
